import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import SEO from '../components/SEO';
import { Eye, EyeSlash, Spinner, Gift, CheckCircle, EnvelopeSimple, CaretDown, CaretUp } from '@phosphor-icons/react';
import { trackEvent, EVT } from '../utils/analytics';

import API_URL from '../utils/apiUrl';

const SUBJECT_LABEL = { maths: 'Maths', english: 'English', science: 'Science' };

export default function Signup() {
  const [searchParams] = useSearchParams();
  const urlReferral = searchParams.get('ref') || '';
  const inviteToken = searchParams.get('invite') || '';
  // Free-lesson context (set by FreeLesson signup gate via querystring)
  const sourceParam = searchParams.get('source') || '';
  const sourceSubject = searchParams.get('subject') || '';
  const sourceScore = searchParams.get('score') || '';
  const fromFreeLesson = sourceParam === 'free_lesson' && SUBJECT_LABEL[sourceSubject];

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'parent',
    referralCode: urlReferral,
  });
  const [showPassword, setShowPassword] = useState(false);
  // Keep optional referral details hidden by default to keep signup minimal.
  // We auto-expand them if the user arrived from a parent CTA or with an invite/referral.
  const [showAdvanced, setShowAdvanced] = useState(
    Boolean(inviteToken) || Boolean(urlReferral)
  );
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingVerification, setPendingVerification] = useState(null); // holds email once register succeeds with verification gate
  const [resendStatus, setResendStatus] = useState(null); // 'sent' | 'cooldown' | error message
  const [referralValid, setReferralValid] = useState(null);
  const [referrerName, setReferrerName] = useState('');
  const { register } = useAuth();
  const navigate = useNavigate();

  // Validate referral code

  // Fire pending_verification_view exactly once when we enter the inbox-wait state
  useEffect(() => {
    if (pendingVerification) {
      trackEvent(EVT.PENDING_VERIFICATION_VIEW, { source: sourceParam || 'direct' });
    }
  }, [pendingVerification, sourceParam]);

  useEffect(() => {
    const code = formData.referralCode.trim();
    if (code.length < 4) { setReferralValid(null); return; }
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`${API_URL}/api/referral/validate/${encodeURIComponent(code)}`);
        if (res.ok) {
          const data = await res.json();
          setReferralValid(data.valid);
          setReferrerName(data.referrer_name || '');
        }
      } catch { setReferralValid(null); }
    }, 500);
    return () => clearTimeout(timer);
  }, [formData.referralCode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.email.trim()) {
      setError('Please enter your email address.');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    trackEvent(EVT.SIGNUP_SUBMIT, { role: 'parent', source: sourceParam || 'direct' });

    const userData = {
      name: formData.name,
      password: formData.password,
      role: 'parent',
      referral_code: formData.referralCode.trim() || null,
    };
    userData.email = formData.email.trim();

    const result = await register(userData);

    if (result.success) {
      // Hard email verification gate — show "check your inbox" state instead of redirecting
      if (result.pending_verification) {
        setPendingVerification(result.user?.email || formData.email.trim());
        setLoading(false);
        return;
      }
      trackEvent(EVT.SIGNUP_COMPLETE, { role: 'parent', source: sourceParam || 'direct' });
      // Auto-accept co-parent invite if present
      if (inviteToken && result.user.role === 'parent') {
        try {
          await fetch(`${API_URL}/api/parent/accept-invite/${inviteToken}`, {
            method: 'POST', credentials: 'include',
          });
        } catch { /* invite acceptance failed, user can still accept from dashboard */ }
      }
      navigate('/parent/dashboard');
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const handleResendVerification = async () => {
    if (!pendingVerification) return;
    setResendStatus('sending');
    try {
      const res = await fetch(`${API_URL}/api/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: pendingVerification }),
      });
      if (res.status === 429) {
        setResendStatus('cooldown');
      } else if (res.ok) {
        setResendStatus('sent');
      } else {
        const body = await res.json().catch(() => ({}));
        setResendStatus(body?.detail || 'Could not resend — try again in a minute.');
      }
    } catch {
      setResendStatus('Network error — try again.');
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-12">
      <SEO
        title="Create your free Grade9Ready account"
        description="Sign up free for AI-marked GCSE revision in Maths, English and Science. No card required — start your first lesson in seconds."
        path="/signup"
      />
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-heading font-bold mb-2" data-testid="signup-heading">
            {fromFreeLesson ? `Create your parent account to continue ${SUBJECT_LABEL[sourceSubject]}` : 'Create your parent account'}
          </h1>
          <p className="text-muted-foreground">
            {fromFreeLesson
              ? `You scored ${sourceScore || '?'} / 3. Create your parent account and we'll take you straight back into ${SUBJECT_LABEL[sourceSubject]}.`
              : 'Set up a parent account, start with the free sampler, or choose a plan later. The number of pupil accounts you can add depends on your subscription.'}
          </p>
        </div>

        <div className="neo-card">
          {/* Pending email verification state — hard gate after successful register */}
          {pendingVerification ? (
            <div className="py-2" data-testid="pending-verification-state">
              <div className="text-center mb-5">
                <div className="mx-auto mb-4 w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
                  <EnvelopeSimple size={36} weight="fill" className="text-primary" />
                </div>
                <h2 className="text-2xl font-heading font-bold mb-2">Check your inbox</h2>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  We&rsquo;ve sent a verification link to <strong className="text-foreground">{pendingVerification}</strong>.
                  <br />Once confirmed, you&rsquo;ll go straight into your first GCSE lesson.
                </p>
              </div>

              <div className="neo-card !p-4 mb-5 bg-emerald-50/50 dark:bg-emerald-900/10 border-emerald-200 dark:border-emerald-900/50" data-testid="pending-verification-readybox">
                <p className="text-xs font-black uppercase tracking-wide text-emerald-700 dark:text-emerald-300 mb-2">While you wait — your account is ready</p>
                <ul className="space-y-1.5 text-sm">
                  <li className="flex items-start gap-2"><CheckCircle size={16} weight="fill" className="text-emerald-500 mt-0.5 shrink-0" /> Your 7-day trial is active</li>
                  <li className="flex items-start gap-2"><CheckCircle size={16} weight="fill" className="text-emerald-500 mt-0.5 shrink-0" /> Your progress will be saved</li>
                  <li className="flex items-start gap-2"><CheckCircle size={16} weight="fill" className="text-emerald-500 mt-0.5 shrink-0" /> 5 AI marking credits ready to use</li>
                </ul>
              </div>

              <div className="neo-card bg-amber-50 dark:bg-amber-900/10 p-4 text-left text-xs text-amber-900 dark:text-amber-200 mb-5">
                <p className="font-bold mb-1">Didn&rsquo;t get it?</p>
                <ul className="list-disc pl-4 space-y-1 leading-relaxed">
                  <li>Check your spam or promotions folder.</li>
                  <li>The link expires in 24 hours.</li>
                  <li>Still nothing after a minute? Tap &ldquo;Resend email&rdquo; below.</li>
                </ul>
              </div>
              <button
                type="button"
                onClick={() => {
                  trackEvent(EVT.VERIFICATION_RESEND_CLICK, { email: pendingVerification });
                  handleResendVerification();
                }}
                className="neo-btn-secondary w-full mb-3"
                disabled={resendStatus === 'sending'}
                data-testid="resend-verification-btn"
              >
                {resendStatus === 'sending' ? 'Sending...' : 'Resend email'}
              </button>
              {resendStatus === 'sent' && (
                <p className="text-xs text-emerald-600 dark:text-emerald-400 font-bold text-center" data-testid="resend-status-sent">
                  Another verification email is on its way.
                </p>
              )}
              {resendStatus === 'cooldown' && (
                <p className="text-xs text-amber-600 dark:text-amber-400 font-bold text-center">
                  Please wait a minute before requesting another email.
                </p>
              )}
              {typeof resendStatus === 'string' && !['sending', 'sent', 'cooldown'].includes(resendStatus) && (
                <p className="text-xs text-destructive font-bold text-center">{resendStatus}</p>
              )}
              <div className="flex items-center justify-between mt-4 text-sm">
                <Link to="/login" className="text-primary font-bold hover:underline" data-testid="back-to-login-link">
                  Back to login
                </Link>
                <button
                  type="button"
                  onClick={() => { setPendingVerification(null); setResendStatus(null); }}
                  className="text-muted-foreground hover:text-foreground underline"
                  data-testid="pending-verification-restart"
                >
                  Changed your email? Start again
                </button>
              </div>
            </div>
          ) : (
          <>
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="p-4 bg-destructive/10 border-2 border-destructive rounded-lg text-destructive text-sm" data-testid="signup-error">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="name" className="block text-sm font-bold mb-2">
                Full Name
              </label>
              <input
                id="name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleChange}
                className="neo-input w-full"
                placeholder="Your name"
                required
                data-testid="signup-name"
              />
            </div>

            <div>
                <label htmlFor="email" className="block text-sm font-bold mb-2">
                  Email Address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="neo-input w-full"
                  placeholder="you@example.com"
                  required
                  data-testid="signup-email"
                />
                <p className="text-xs text-muted-foreground mt-1.5">
                  We&rsquo;ll send a confirmation link to this address — click it to finish setting up your parent account.
                </p>
              </div>

            {/* Referral Code — only visible when advanced section is expanded */}
            {showAdvanced && (
            <div>
              <label htmlFor="referralCode" className="block text-sm font-bold mb-2">
                Referral Code <span className="text-muted-foreground font-normal">(optional)</span>
              </label>
              <div className="relative">
                <input
                  id="referralCode"
                  name="referralCode"
                  type="text"
                  value={formData.referralCode}
                  onChange={handleChange}
                  className="neo-input w-full pr-10"
                  placeholder="e.g. G9R-ABC123"
                  data-testid="signup-referral"
                />
                {referralValid === true && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <CheckCircle size={20} weight="fill" className="text-emerald-500" />
                  </div>
                )}
              </div>
              {referralValid === true && referrerName && (
                <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1" data-testid="referral-valid">
                  <Gift size={12} weight="fill" />
                  Referred by {referrerName} — you both get a reward!
                </p>
              )}
              {referralValid === false && formData.referralCode.trim().length >= 4 && (
                <p className="text-xs text-destructive mt-1" data-testid="referral-invalid">Invalid referral code</p>
              )}
            </div>
            )}

            <div>
              <label htmlFor="password" className="block text-sm font-bold mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  className="neo-input w-full pr-12"
                  placeholder="At least 6 characters"
                  required
                  minLength={6}
                  data-testid="signup-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {/* Optional referral details */}
            <button
              type="button"
              onClick={() => setShowAdvanced((v) => !v)}
              className="text-sm font-bold text-primary hover:underline flex items-center gap-1.5"
              data-testid="signup-toggle-advanced"
            >
              {showAdvanced ? <CaretUp size={14} weight="bold" /> : <CaretDown size={14} weight="bold" />}
              {showAdvanced ? 'Hide referral option' : 'Have a referral code?'}
            </button>

            <button
              type="submit"
              disabled={loading}
              className="neo-btn w-full flex items-center justify-center gap-2"
              data-testid="signup-submit"
            >
              {loading ? (
                <>
                  <Spinner size={20} className="animate-spin" />
                  Creating account...
                </>
              ) : (
                'Create Account'
              )}
            </button>
          </form>

          <div className="mt-6 text-center text-sm">
            <p className="text-muted-foreground">
              Already have an account?{' '}
              <Link to="/login" className="font-bold text-primary hover:underline" data-testid="login-link">
                Log in
              </Link>
            </p>
          </div>
          </>
          )}
        </div>
      </div>
    </div>
  );
}
